import { useState } from 'react';
import { useConnect, useAccount } from 'wagmi';
import { useWeb3Modal } from '@web3modal/wagmi/react';
import { motion, AnimatePresence } from 'framer-motion';
import { ethers } from 'ethers';
import '../App.css';

const WalletModal = ({ isOpen, onClose }) => {
  const { connect, connectors } = useConnect();
  const { isConnected } = useAccount();
  const { open } = useWeb3Modal();
  const [connecting, setConnecting] = useState(null);

  const wallets = [
    {
      id: 'trust',
      name: 'Trust Wallet',
      logo: 'https://trustwallet.com/assets/images/media/assets/TWT.png',
      description: 'Mobile & Browser',
      color: '#3375BB',
      type: 'injected'
    },
    {
      id: 'safepal',
      name: 'SafePal',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/8119.png',
      description: 'Hardware & Software',
      color: '#6C4FE8',
      type: 'injected'
    },
    {
      id: 'metamask',
      name: 'MetaMask',
      logo: 'https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg',
      description: 'Popular Browser Wallet',
      color: '#F6851B',
      type: 'injected'
    },
    {
      id: 'walletconnect',
      name: 'All Wallets',
      logo: '/assets/wallets/walletconnect-logo-new.png',
      description: 'Connect to 200+ wallets',
      color: '#3B99FC',
      type: 'walletconnect'
    }
  ];

  const connectInjectedWallet = async (walletId) => {
    setConnecting(walletId);
    
    try {
      // Check if ethereum provider exists
      if (!window.ethereum) {
        const walletName = walletId === 'metamask' ? 'MetaMask' : 
                          walletId === 'trust' ? 'Trust Wallet' : 'SafePal';
        alert(`Please install ${walletName} browser extension first.`);
        setConnecting(null);
        return;
      }

      // Modern approach using ethers v6 and wagmi
      const injectedConnector = connectors.find(c => 
        c.id === 'injected' || c.type === 'injected'
      );

      if (!injectedConnector) {
        throw new Error('Injected connector not found');
      }

      // For Trust Wallet - ensure proper provider detection
      if (walletId === 'trust') {
        // Trust Wallet specific handling
        if (window.ethereum.isTrust || window.trustwallet) {
          await connect({ connector: injectedConnector });
        } else if (window.ethereum) {
          // Fallback to generic ethereum provider
          await connect({ connector: injectedConnector });
        } else {
          alert('Please open this dApp in Trust Wallet browser or install Trust Wallet extension.');
          setConnecting(null);
          return;
        }
      } else if (walletId === 'safepal') {
        // SafePal specific handling
        if (window.ethereum.isSafePal || window.safepal) {
          await connect({ connector: injectedConnector });
        } else {
          await connect({ connector: injectedConnector });
        }
      } else if (walletId === 'metamask') {
        // MetaMask specific handling
        if (window.ethereum.isMetaMask) {
          await connect({ connector: injectedConnector });
        } else {
          await connect({ connector: injectedConnector });
        }
      }

      onClose();
    } catch (error) {
      console.error('Connection error:', error);
      if (!error.message?.includes('User rejected') && 
          !error.message?.includes('User cancelled') &&
          !error.message?.includes('Request of type')) {
        alert('Connection failed. Please make sure you have the wallet installed and try again.');
      }
    } finally {
      setConnecting(null);
    }
  };

  const connectWalletConnect = async () => {
    setConnecting('walletconnect');
    
    try {
      onClose(); // Close our custom modal first
      await open(); // Open Web3Modal which shows 200+ wallets
    } catch (error) {
      console.error('WalletConnect error:', error);
      if (!error.message?.includes('User rejected') && 
          !error.message?.includes('User cancelled')) {
        // Silently fail - user might have closed modal
      }
    } finally {
      setConnecting(null);
    }
  };

  const handleConnect = async (wallet) => {
    if (wallet.type === 'injected') {
      await connectInjectedWallet(wallet.id);
    } else {
      await connectWalletConnect();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="wallet-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="wallet-modal"
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: 'spring', duration: 0.5 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="wallet-modal-header">
            <div>
              <h2 className="wallet-modal-title">Connect Wallet</h2>
              <p className="wallet-modal-subtitle">Choose your preferred wallet</p>
            </div>
            <button className="wallet-modal-close" onClick={onClose}>
              <span>✕</span>
            </button>
          </div>

          <div className="wallet-modal-content">
            {wallets.map((wallet, index) => (
              <motion.button
                key={wallet.id}
                className="wallet-option"
                onClick={() => handleConnect(wallet)}
                disabled={connecting !== null}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02, x: 4 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="wallet-option-logo">
                  <img 
                    src={wallet.logo} 
                    alt={wallet.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div className="wallet-option-fallback" style={{ display: 'none' }}>
                    {wallet.id === 'trust' ? '◈' : 
                     wallet.id === 'safepal' ? '◉' : 
                     wallet.id === 'metamask' ? '◆' : '⬢'}
                  </div>
                </div>
                <div className="wallet-option-info">
                  <div className="wallet-option-name">{wallet.name}</div>
                  <div className="wallet-option-desc">{wallet.description}</div>
                </div>
                {connecting === wallet.id ? (
                  <div className="wallet-option-loading">
                    <div className="spinner"></div>
                  </div>
                ) : (
                  <div className="wallet-option-arrow">→</div>
                )}
              </motion.button>
            ))}
          </div>

          <div className="wallet-modal-footer">
            <div className="wallet-modal-footer-text">
              <span className="wallet-footer-icon">◈</span>
              <span>Secure connection {connecting && connecting !== 'walletconnect' ? 'via Injected' : 'via WalletConnect'}</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WalletModal;
